import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, CheckCircle2, Smartphone, MessageCircle } from "lucide-react";
import { requestAppLink } from "../../lib/api";
import { track } from "../../lib/analytics";
import { Spark } from "./decor";
import { CountrySelect, COUNTRIES, type Country } from "./CountrySelect";

const GB = COUNTRIES.find((c) => c.iso === "GB")!;

const GOAL_PRESETS = [
  "Lose 5 kg",
  "Fit my old jeans",
  "Feel more energetic",
  "Build a daily habit",
  "Support my medication",
];

type OpenOpts = { source?: string };
type Ctx = { open: (opts?: OpenOpts) => void };

const GetAppContext = createContext<Ctx>({ open: () => {} });

/** Any CTA can pop the "Get the app" form via `useGetApp().open()`. */
export function useGetApp() {
  return useContext(GetAppContext);
}

type Status = "idle" | "loading" | "success" | "error";

export function GetAppProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState("cta");

  const open = useCallback((opts?: OpenOpts) => {
    setSource(opts?.source ?? "cta");
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <GetAppContext.Provider value={{ open }}>
      {children}
      <GetAppDialog isOpen={isOpen} source={source} onClose={close} />
    </GetAppContext.Provider>
  );
}

function GetAppDialog({
  isOpen,
  source,
  onClose,
}: {
  isOpen: boolean;
  source: string;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<Status>("idle");
  const [name, setName] = useState("");
  const [country, setCountry] = useState<Country>(GB);
  const [phone, setPhone] = useState("");
  const [goal, setGoal] = useState("");
  const [goalCustom, setGoalCustom] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const digits = (phone.match(/\d/g) ?? []).length;

  // Reset to a clean form each time the dialog opens, and focus the first field.
  useEffect(() => {
    if (!isOpen) return;
    setStatus("idle");
    setName("");
    setCountry(GB);
    setPhone("");
    setGoal("");
    setGoalCustom(false);
    setErrorMsg("");
    setSmsSent(false);
    const t = setTimeout(() => nameRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Close on Escape and lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "loading") return;

    if (name.trim().length < 1) {
      setStatus("error");
      setErrorMsg("Please tell us your name.");
      return;
    }
    if (digits < 7) {
      setStatus("error");
      setErrorMsg("Please enter a valid mobile number.");
      return;
    }

    setStatus("loading");
    setErrorMsg("");
    try {
      const result = await requestAppLink({
        name: name.trim(),
        // Combine to an international number; drop a single leading trunk "0"
        // (common in UK/EU local formats) since the dial code replaces it.
        phone: `${country.dial} ${phone.trim().replace(/^0+/, "")}`,
        goal: goal.trim() || undefined,
        source,
      });
      if ("notConfigured" in result) {
        setStatus("error");
        setErrorMsg("We can't take details just yet — try again soon.");
        return;
      }
      setSmsSent(Boolean(result.smsSent));
      setStatus("success");
      track("get_app_submit", { source, sms: Boolean(result.smsSent) });
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error && err.message
          ? err.message
          : "Something went wrong — try again."
      );
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="getapp-title"
        >
          {/* Scrim */}
          <button
            aria-label="Close"
            onClick={onClose}
            className="absolute inset-0 bg-navy/50 backdrop-blur-[2px]"
          />

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="card-pop relative z-10 w-full max-w-md rounded-b-none rounded-t-[1.75rem] bg-cream p-6 sm:rounded-[1.75rem] sm:p-8"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border-2 border-navy bg-white text-navy transition-transform hover:rotate-90"
            >
              <X className="h-4 w-4" strokeWidth={3} />
            </button>

            {status === "success" ? (
              <div className="flex flex-col items-center py-4 text-center" role="status" aria-live="polite">
                <Spark fill="#FFC93C" className="absolute -left-3 -top-3 rotate-12" />
                <span className="flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-navy bg-sun text-navy shadow-pop-xs">
                  <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
                </span>
                <h3 id="getapp-title" className="display mt-5 text-2xl text-navy">
                  You&apos;re all set, {name.trim().split(" ")[0]}!
                </h3>
                <p className="mt-3 text-charcoal/75">
                  {smsSent
                    ? "We've just texted a download link to your phone. See you in the app!"
                    : "Thanks — we've saved your details and we'll text you a download link shortly. See you in the app!"}
                </p>
                <button onClick={onClose} className="btn-primary mt-6 px-7 py-3 text-base">
                  Done
                </button>
              </div>
            ) : (
              <>
                <span className="sticker -rotate-2">
                  <Smartphone className="h-4 w-4" /> Get the app
                </span>
                <h3 id="getapp-title" className="display mt-4 text-2xl text-navy sm:text-3xl">
                  Send me the app
                </h3>
                <p className="mt-2 text-sm text-charcoal/70">
                  Pop in your details and we&apos;ll text a download link straight
                  to your phone. It&apos;s free, no card needed.
                </p>

                <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
                  <div>
                    <label htmlFor="getapp-name" className="font-display text-sm font-bold text-navy">
                      Your name
                    </label>
                    <input
                      id="getapp-name"
                      ref={nameRef}
                      type="text"
                      name="name"
                      autoComplete="given-name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (status === "error") setStatus("idle");
                      }}
                      placeholder="Alex"
                      className="mt-1.5 w-full rounded-2xl border-[3px] border-navy bg-white px-4 py-3 text-navy placeholder-charcoal/40 outline-none transition focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-cream"
                    />
                  </div>

                  <div>
                    <label htmlFor="getapp-phone" className="font-display text-sm font-bold text-navy">
                      Mobile number
                    </label>
                    <div className="mt-1.5 flex gap-2">
                      <CountrySelect value={country} onChange={setCountry} />
                      <input
                        id="getapp-phone"
                        type="tel"
                        name="phone"
                        autoComplete="tel-national"
                        inputMode="tel"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (status === "error") setStatus("idle");
                        }}
                        placeholder="07123 456789"
                        className="w-full flex-1 rounded-2xl border-[3px] border-navy bg-white px-4 py-3 text-navy placeholder-charcoal/40 outline-none transition focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-cream"
                      />
                    </div>
                  </div>

                  <div>
                    <span className="font-display text-sm font-bold text-navy">
                      Your goal <span className="font-body font-normal text-charcoal/50">(optional)</span>
                    </span>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {GOAL_PRESETS.map((preset) => {
                        const selected = !goalCustom && goal === preset;
                        return (
                          <button
                            key={preset}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => {
                              setGoalCustom(false);
                              setGoal(selected ? "" : preset);
                            }}
                            className={`inline-flex min-h-[2.5rem] cursor-pointer items-center rounded-full border-2 border-navy px-4 py-2 text-sm font-bold transition ${
                              selected
                                ? "bg-teal text-white"
                                : "bg-white text-navy hover:bg-teal-mist"
                            }`}
                          >
                            {preset}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        aria-pressed={goalCustom}
                        onClick={() => {
                          setGoalCustom(true);
                          setGoal("");
                        }}
                        className={`inline-flex min-h-[2.5rem] cursor-pointer items-center rounded-full border-2 border-navy px-4 py-2 text-sm font-bold transition ${
                          goalCustom
                            ? "bg-teal text-white"
                            : "bg-white text-navy hover:bg-teal-mist"
                        }`}
                      >
                        Something else
                      </button>
                    </div>
                    {goalCustom && (
                      <input
                        id="getapp-goal"
                        type="text"
                        name="goal"
                        autoFocus
                        value={goal}
                        onChange={(e) => setGoal(e.target.value)}
                        placeholder="e.g. lose 5 kg before summer"
                        className="mt-2.5 w-full rounded-2xl border-[3px] border-navy bg-white px-4 py-3 text-navy placeholder-charcoal/40 outline-none transition focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-cream"
                      />
                    )}
                  </div>

                  {status === "error" && (
                    <p className="text-sm font-bold text-red-600" role="alert">
                      {errorMsg}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="btn-primary w-full py-3.5 text-base disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
                      </>
                    ) : (
                      <>
                        <MessageCircle className="h-4 w-4" /> Text me the link
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-charcoal/55">
                    We&apos;ll only text you about FitBuddy. No spam, ever.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
